import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import WebApp from '@twa-dev/sdk'

interface WebAppUser {
  id: number
  first_name?: string
  last_name?: string
  username?: string
  language_code?: string
}

export interface AuthUser {
  _id?: string
  userName: string
  telegramId: string
}

interface AuthSession {
  tokenType: string
  accessToken: string
  lifetimeAccessToken: number
  refreshToken: string
  lifetimeRefreshToken: number
}

type AuthStatus = 'loading' | 'authenticated' | 'error'

interface UserContextValue {
  status: AuthStatus
  isAuthenticated: boolean
  userTg: WebAppUser | null
  userBackend: AuthUser | null
  errorMessage: string | null
  retry: () => Promise<void>
}

const UserContext = createContext<UserContextValue>({
  status: 'loading',
  isAuthenticated: false,
  userTg: null,
  userBackend: null,
  errorMessage: null,
  retry: async () => {}
})

const getUserApiBase = () => {
  if (import.meta.env.DEV) {
    return `${import.meta.env.VITE_DOMAIN}/userprofile/api/v1`
  }

  return '/userprofile/api/v1'
}

const parseDevInitDataUnsafe = (initDataRaw: string): { user?: WebAppUser } => {
  try {
    const query = new URLSearchParams(initDataRaw)
    const userRaw = query.get('user')
    if (!userRaw) {
      return {}
    }

    return { user: JSON.parse(userRaw) as WebAppUser }
  } catch {
    return {}
  }
}

const fetchJson = async <T,>(input: RequestInfo | URL, init?: RequestInit) => {
  const response = await fetch(input, init)

  if (response.status === 404) {
    return null
  }

  const payload = await response.json()

  if (response.status >= 400) {
    throw new Error(payload?.message || 'Request failed')
  }

  return payload as T
}

const getUserByTgId = async (telegramId: number): Promise<AuthUser | null> => {
  const url = `${getUserApiBase()}/users/${telegramId}/by-telegram-id`
  return fetchJson<AuthUser>(url)
}

const createUser = async (initData: string): Promise<{ user: AuthUser } | null> => {
  const url = `${getUserApiBase()}/users`
  return fetchJson<{ user: AuthUser }>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData })
  })
}

const authenticateUser = async (initData: string): Promise<AuthSession | null> => {
  const url = `${getUserApiBase()}/users/auth`
  return fetchJson<AuthSession>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData })
  })
}

const UserProvider = ({ children }: React.PropsWithChildren) => {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [userTg, setUserTg] = useState<WebAppUser | null>(null)
  const [userBackend, setUserBackend] = useState<AuthUser | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const runAuth = useCallback(async () => {
    setStatus('loading')
    setErrorMessage(null)

    try {
      WebApp.ready()
      WebApp.expand()
    } catch {
      // Continue auth even if app APIs are unavailable outside Telegram.
    }

    const initDataRaw =
      (import.meta.env.DEV ? import.meta.env.VITE_INIT_DATA : '') || WebApp.initData
    const initDataUnsafe =
      (import.meta.env.DEV ? parseDevInitDataUnsafe(initDataRaw) : null) ||
      WebApp.initDataUnsafe
    const telegramUser = initDataUnsafe?.user ?? null

    if (!telegramUser || !initDataRaw) {
      setErrorMessage('Telegram init data is missing. Open the app from Telegram.')
      setStatus('error')
      return
    }

    setUserTg(telegramUser)

    try {
      const existingUser = await getUserByTgId(telegramUser.id)

      if (existingUser) {
        setUserBackend(existingUser)
        await authenticateUser(initDataRaw)
      } else {
        const created = await createUser(initDataRaw)
        if (created?.user) {
          setUserBackend(created.user)
        }
      }

      setStatus('authenticated')
    } catch (error) {
      setErrorMessage((error as Error).message || 'Authentication failed')
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    runAuth()
  }, [runAuth])

  const value = useMemo<UserContextValue>(
    () => ({
      status,
      isAuthenticated: status === 'authenticated',
      userTg,
      userBackend,
      errorMessage,
      retry: runAuth
    }),
    [status, userTg, userBackend, errorMessage, runAuth]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export const useUser = () => useContext(UserContext)

export default UserProvider
